import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import {
  Box, Tab, TextField, MenuItem, Grid, Card, CardContent, Typography, Button,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { KC_NAME } from '../../../constants/graphs';
import {
  GetCultivationCyclesBySectorResponse,
  CultivationCyclePopulatedSchema,
} from '../../../requests/api/apiTypes';
import { ReduxState } from '../../../types';
import ConversationsTab from '../ConversationTab';
import useStyles from '../styles';
import apiClient from '../../../requests/api/apiClient';
import useFetchApi from '../../../hooks/useFetchApi';
import { ModalState, EMPTY_MODAL_STATE } from '../chartModal';
import useIsClientOperator from '../../../hooks/useIsClientOperator';

const KcConfigContent = ({
  sectorId,
  setModalState: setModalStateProp,
  refreshCounter = 0,
}: {
  sectorId: string;
  setModalState?: React.Dispatch<React.SetStateAction<ModalState>>;
  refreshCounter?: number;
}) => {
  const classes = useStyles();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);

  // Default to the conversations tab when arriving from a notification (date param).
  const date = queryParams.get('date');
  const initialTabValue = date ? '2' : '1';

  const [tabValue, setTabValue] = useState(initialTabValue);
  const { selectedSector } = useSelector((state: ReduxState) => state.currentSector);
  const { enqueueSnackbar } = useSnackbar();

  const [, setInternalModalState] = useState<ModalState>(EMPTY_MODAL_STATE);
  const setModalState = setModalStateProp ?? setInternalModalState;

  const [cultivationCycles, setCultivationCycles] = useState<CultivationCyclePopulatedSchema[]>([]);
  const [selectedCycle, setSelectedCycle] = useState<CultivationCyclePopulatedSchema | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isClientOperator = useIsClientOperator();

  useEffect(() => {
    if (isClientOperator && tabValue === '1') {
      setTabValue('2');
    }
  }, [isClientOperator, tabValue]);

  const { data: cultivationCyclesData } = useFetchApi<GetCultivationCyclesBySectorResponse>({
    modelEndpoint: 'cultivationCycleSectorDetail',
    modelName: 'cultivationCycleSector',
    id: sectorId,
    dependencies: [sectorId],
  });

  const { data: sectorDetail } = useFetchApi<{
    sector: {
      irrigationConfig?: {
        selectedCultivationCycleId?: string;
      };
    };
  }>({
    modelEndpoint: 'sectorDetail',
    modelName: 'sector',
    id: sectorId,
    dependencies: [sectorId],
  });

  useEffect(() => {
    if (cultivationCyclesData) {
      setCultivationCycles(cultivationCyclesData.cultivationCycles);
      if (cultivationCyclesData.cultivationCycles.length > 0) {
        const savedCycleId = sectorDetail?.sector?.irrigationConfig?.selectedCultivationCycleId;
        const savedCycle = savedCycleId
          ? cultivationCyclesData.cultivationCycles.find((c) => c.id === savedCycleId)
          : null;
        setSelectedCycle(savedCycle || cultivationCyclesData.cultivationCycles[0]);
      }
    }
  }, [cultivationCyclesData, sectorDetail]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const handleCycleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedCycleName = event.target.value;
    const cycle = cultivationCycles.find((c) => c.name === selectedCycleName);
    setSelectedCycle(cycle || null);
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    if (!selectedCycle || !sectorId) return;

    setIsSaving(true);
    try {
      await apiClient.sector.sectorUpdate(sectorId, {
        name: selectedSector?.name || ' ',
        theoreticalSurface: selectedSector?.theoreticalSurface || '0',
        typeOfSoil: selectedSector?.typeOfSoil || ' ',
        irrigationConfig: {
          ...selectedSector?.irrigationConfig,
          selectedCultivationCycleId: selectedCycle.id,
        },
      });

      setHasUnsavedChanges(false);
      enqueueSnackbar('Ciclo de cultivo guardado exitosamente', { variant: 'success' });
    } catch (err) {
      console.error('Error saving selected cultivation cycle:', err);
      enqueueSnackbar('Error al guardar el ciclo de cultivo seleccionado', { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <TabContext value={tabValue}>
      <Box sx={{
        borderBottom: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <TabList onChange={handleTabChange} aria-label="Kc chart tabs" className={classes.tabList}>
          {!isClientOperator && (
            <Tab label="Configuración" value="1" className={classes.tabLabel} />
          )}
          <Tab label="Conversaciones" value="2" className={classes.tabLabel} />
        </TabList>
      </Box>
      {!isClientOperator && (
        <TabPanel value="1" className={classes.configTabPanel}>
          <Grid container spacing={3} sx={{ width: '100%' }}>
            <Grid item xs={4}>
              <Card>
                <CardContent>
                  <Box>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      Ciclo de Cultivo
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Selecciona el ciclo de cultivo.
                    </Typography>
                    <TextField
                      label="Cultivo"
                      select
                      fullWidth
                      value={cultivationCycles.length > 0 ? selectedCycle?.name || 'No hay cultivos' : 'No hay cultivos'}
                      variant="standard"
                      onChange={handleCycleChange}
                      disabled={cultivationCycles.length === 0}
                    >
                      {cultivationCycles.length > 0 ? (
                        cultivationCycles.map((cycle) => (
                          <MenuItem key={cycle.id} value={cycle.name}>
                            {cycle.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="No hay cultivos">
                          No hay ciclos de cultivo
                        </MenuItem>
                      )}
                    </TextField>
                    <Box mt={2}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        disabled={!hasUnsavedChanges || isSaving || !selectedCycle}
                        fullWidth
                      >
                        {isSaving ? 'Guardando...' : 'Guardar Ciclo de Cultivo'}
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      )}
      <TabPanel value="2" className={classes.configTabPanel}>
        <ConversationsTab
          setModalState={setModalState}
          sectorId={sectorId}
          chartName={KC_NAME}
          refreshCounter={refreshCounter}
        />
      </TabPanel>
    </TabContext>
  );
};

export default KcConfigContent;
